import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';

import AddressOverview from '../../../../../components/AddressOverview';
import { getUsableAddresses } from '../../../../../components/AddressOverview/getUsableAddresses';
import InformationOverlay from '../../../../../components/InformationOverlay';

/**
 * Billing Address Selector Component
 *
 * Lets the user pick a billing address from their unified address book, mirroring
 * the delivery AddressSelectOverlay. On select it populates the billing_* Formik
 * fields through the shared, prefix-parameterised selectAddress helper
 * (prefix 'billing_'), so there is a single populator for delivery and billing.
 *
 * @component
 */

interface BillingAddressSelectorProps {
  /** Callback to update the selected billing address index */
  setSelectedBillingAddressIndex: (index: number) => void;
  /** React ref to the checkout form */
  // TODO: Replace 'any' with proper Formik ref type when available
  formRef: React.RefObject<any>;
  /** Currently selected billing address index, or null if none selected */
  selectedBillingAddressIndex: number | null;
  /** Shared populator; called with the 'billing_' prefix */
  // TODO: Replace 'any' with proper address type definition
  selectAddress: (
    ref: React.RefObject<any>,
    address: any,
    prefix?: string
  ) => void;
}

const BillingAddressSelector: React.FC<BillingAddressSelectorProps> = ({
  setSelectedBillingAddressIndex,
  formRef,
  selectedBillingAddressIndex,
  selectAddress,
}) => {
  const { t } = useTranslation();
  const uid = useSelector((state: any) => state.getIn(['login', 'uid'], null));
  const addressesRaw = useSelector((state: any) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const { usableAddresses: usableProfileAddresses } =
    getUsableAddresses(addressesRaw);
  const addressSelected = selectedBillingAddressIndex !== null;

  return (
    <InformationOverlay
      buttonClassName={`Button--text ${
        addressSelected ? 'Button--edit' : 'Button--add Button--inverted'
      }`}
      overlayKey="checkout_billing_address_overview"
      buttonIcon={addressSelected ? 'edit' : 'plus'}
      buttonTitle={
        addressSelected
          ? t('address.change_address')
          : t('address.choose_billing_from_addressbook')
      }
      disableClosers
    >
      <AddressOverview
        selectionMode
        title={
          usableProfileAddresses.size === 0
            ? t('address.first_delivery_address')
            : t('address.choose_billing_address')
        }
        activeIndex={Number(selectedBillingAddressIndex)}
        onSelect={(index: number) => {
          const formSelectedAddress = addressesRaw.get(index);
          setSelectedBillingAddressIndex(index);
          selectAddress(formRef, formSelectedAddress, 'billing_');
        }}
      />
    </InformationOverlay>
  );
};

export default BillingAddressSelector;
